import { useState } from 'react'
import { cloneDeep, isEmpty, merge } from "lodash"
import { ConvertDate, ConvertDateTime, Now, NowDB, TodayDB } from '../utils.ts'

import { Box, Button, Header, Modal, SpaceBetween, Table, Link, Pagination, Input, Grid, Container, Alert, Multiselect } from '@cloudscape-design/components'

import { ActivityRecord } from '../models/Activity'
import { SESSION_SERVICE } from "../services/SessionService"
import LabeledTextarea from '../widgets/LabeledTextarea.tsx'
import ComboInput from '../widgets/ComboInput.tsx'
import DateInput from "../widgets/DateInput"
import useList from '../hooks/useList.ts'
import { useQueryData, upsert, deleteId } from '../hooks/useQueryData.ts'
import useUserNames from '../hooks/useUserNames'
import SelectInput from '../widgets/SelectInput.tsx'
import DateTimeInput from '../widgets/DateTimeInput.tsx'

const userName = SESSION_SERVICE.getCurrentSession().user?.fullName

export default ({ forId, caseId, numItems = 12, width = "wide" }: { forId: string, caseId: string, numItems?: number, width?: "wide" | "narrow" }) => {
  // const activityOptions = [
  //   {label: "Notes", value: "Note", description: ""},
  //   {label: "Contacts", value: "Contact", description: ""},
  //   {label: "Add Documents", value: "Add Document", description: ""},
  //   {label: "Creates/Updates", value: "Create/Update", description: ""},
  //   {label: "Milestones", value: "Milestone", description: ""},
  // ]
  const [ currentPage, setCurrentPage ] = useState(1)
  const [ pageSize ] = useState(numItems)
  const [ visible, setVisible ] = useState(false)
  const newActivity = {CaseId: caseId, ForId: forId, Type: "Note",  Description: "", On: NowDB(), createdBy: userName, updatedBy: userName} as ActivityRecord
  const [ activity, setActivity ] = useState<ActivityRecord>({ ...newActivity })
  const [ user, setUser ] = useState("All Users")
  // const [selectedOptions, setSelectedOptions] = useState(activityOptions.filter(option => option.value !== "Create/Update"))
  const [ filter, setFilter ] = useState("All Activity")
  let users = useUserNames().data

  const readOnly = false
  const validating = true
  var valid = true

  function isValid(value: string, label: string): boolean {
    const thisValid: boolean = !isEmpty(value?.trim())
    if (validating) {
        if (!thisValid) valid = false
        return thisValid
    }
    return true
  }

  const forType = forId?.split(":")[0]
  let options = [] as string[] | {label: string, value: string}[]
  switch (forType) {
    case "Case":       options = useList("CaseActivity"); break
    case "Litigation": options = useList("LitigationActivity"); break
    case "User":       options = useList("UserActivity"); break
  }
  
  const wheres: {field: string, kind: string, value: string}[] = []
  if (forType === "Case" && caseId) wheres.push({field: 'CaseId', kind: 'equals', value: caseId})
  if (forType !== "Case" && forId ) wheres.push({field: 'ForId', kind: 'equals', value: forId})
  if (user !== "All Users") { wheres.push({field: 'createdBy', kind: 'equals', value: user}) }

  // Filter by Type
  if (filter !== "All Activity") {
    if (filter === "Notes") {
      wheres.push({field: 'Type', kind: 'equals', value: 'Note'})
    } else if (filter === "Contacts") {
      wheres.push({field: 'Type', kind: 'equals', value: 'Contact'})
    } else if (filter === "Documents") {
      wheres.push({field: 'Type', kind: 'equals', value: 'Add Document'})
    }
  }

  const { data, isLoading } = useQueryData('ActivitySortedCaseName', { Paging: {Page: currentPage, PageSize: pageSize}, Where: wheres as any}, !forId?.includes('undefined'))
  // const { data, isLoading } = useQueryData('ActivitySortedCaseName')
  let results = data?.results ?? []
  for (const a of results) {
    if (!a.On) {a.On = a.createdAt}
  }
  const count = data?.count ?? 0

  const close = () => {setVisible(false)}
  const open = (e: ActivityRecord) => {
    setActivity(cloneDeep(e))
    setVisible(true)
  }
  const remove = (e: ActivityRecord) => {
    if (window.confirm("Are you sure you want to delete this Activity?")) {
      deleteId('Activity', e.objectId ?? "")
    }
  }

  const handleChange = (newValues: Object) => {
    setActivity(merge({}, activity, newValues))
  }

  function setDescription(text: string) {
    // Update the Activity's Description property with the provided text
    setActivity({ ...activity, Description: text });
  }

  function saveNote() {
    if (isEmpty(activity?.Description)) {
      alert("Must type a description for this note")
      return
    }
    const newAct = { CaseId: caseId, ForId: forId, Type: activity?.Type ?? "Note", Description: activity?.Description ?? "", On: NowDB(), 
      createdBy: userName, updatedBy: userName } as ActivityRecord
    upsert('Activity', newAct)
    setActivity({ ...newActivity })
  }

  let cd = [
        { id: "OnBy", minWidth: "160px", header: "", cell: (e: any) => ConvertDateTime(e.On) + "\r" + e?.createdBy },
        { id: "Type", width: "180px", header: "Type", cell: (e: any) => e.Type },
        { id: "Description", header: "Description", cell: (e: any) => e.Description },
        { width: "120px", id: "Actions", header: "Actions",  cell: (e: any) => (
          <SpaceBetween size="s" direction="horizontal">
            <Button iconName="edit" ariaLabel="Edit" onClick={() => open(e)} />
            <Button iconName="delete-marker" ariaLabel="Delete" onClick={() => remove(e)} />
          </SpaceBetween>
          ), },
      ]
  if (width === "wide" || forType === "User") cd.splice(3, 0, { width: "200px", id: "Case", header: "Case", cell: (e: any) => e.CaseId ? <a href={`/case/${e.CaseId}`}>{e?.CaseName ? e.CaseName : "Open Case"}</a> : "No Case" })

  return (
    <>
    {/* @ts-ignore */}
    <ActivityDialog activity={activity} visible={visible} handleChange={handleChange} close={close} 
      validate={isValid} isValid={valid} readOnly={readOnly} options={options} />
    <Grid gridDefinition={forId && width === "wide" ? [{ colspan: 9 }, { colspan: 3 }] : [{ colspan: 12 }]}>
    <Table wrapLines data-panel contentDensity="compact" stripedRows
      columnDefinitions={cd}
      items={ results }
      loadingText="Loading Activity"
      loading={ isLoading }
      trackBy="objectId"
      empty="No Activity"
      header={ <Header
        counter={ count.toString() }
        actions={
        <SpaceBetween size="s" direction="horizontal">
          { width !== "wide" ? <>
          <ComboInput label="" value={activity.Type} validate={isValid} readOnly={readOnly}
              options={options}
              onChange={ (v) => handleChange({Type: v}) } />
          <Input data-width='px450' autoComplete={false}
            onChange={({ detail }) => setDescription(detail.value)}
            onKeyDown={event => {event.detail.key === "Enter" && saveNote()}}
            value={activity?.Description ?? ""}
            placeholder="Type description here. Press [Enter]" /> </> : <></> }
          { width === "wide" ? <>
          {/* @ts-ignore */}
            {/* <Multiselect inlineTokens
              selectedOptions={selectedOptions}
              options={activityOptions}
              onChange={({ detail }) => setSelectedOptions(detail.selectedOptions.map(o => {return {label: o.label ?? "", value: o.value ?? "", description: o.description ?? ""}}))}
              >
            </Multiselect> */}
            <SelectInput value={filter}
                options="All Activity;Notes;Contacts;Documents" onChange={ (v: any) => setFilter(v.detail.value) } />
            <SelectInput value={user}
                options={users} onChange={ (v: any) => setUser(v.detail.value) } />
            <Pagination 
              currentPageIndex={currentPage} 
              pagesCount={Math.ceil(count / pageSize)}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex) } /></> : <Button iconName="add-plus" variant="normal" onClick={() => open({ ...newActivity })} /> }
        </SpaceBetween>
        }>Activity</Header> } 
      />
      {forId && width === "wide" ? <ActivityPanel activity={activity} forId={forId ?? ""} caseId={caseId ?? ""} visible={visible} handleChange={handleChange} close={close} 
        validate={isValid} isValid={valid} readOnly={readOnly} options={options} /> : <></>}
      </Grid>
    </>
  )

// function LinkFor(forId: string) {
//   const parts = forId.split(":")
//   const type = parts[0]
//   const id = parts[1]
//   switch (type) {
//     case "Intake":
//       return <Link href={"/intakeform/" + id + "/"}>Goto</Link>
//     case "Case":
//       return <Link href={"/case/" + id + "/"}>Goto</Link>
//     case "Contact":
//       return <Link href={"/contactform/" + id + "/"}>Goto</Link>
//     case "Litigation":
//       return <Link href={"/litigationform/" + id + "/"}>Goto</Link>
//   }
  
//   return forId
// }
}
  
const ActivityDialog = ({activity, visible, handleChange, close, validate, isValid, readOnly, options}: 
  {activity: ActivityRecord, forId: string, caseId: string, visible: boolean, handleChange: (o: object) => void, close: () => void,
  validate: (l: string, v: string) => boolean, isValid: boolean, readOnly: boolean, options: string[] | {label: string, value: string}[]
  }) => {

  function create() {        
    if (isValid) {
        close()
        // @ts-ignore
        activity.CaseName = null
        upsert('Activity', activity)
        handleChange({Type: "Note",  Description: ""} as ActivityRecord)
    }
  }

  return (
    <Modal
      onDismiss={close}
      visible={visible}
      footer={
          <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={event => close()}>Cancel</Button>
              <Button variant="primary" onClick={event => create()}>Ok</Button>
          </SpaceBetween>
          </Box>
      }
      header={activity._id ? "Edit Activity" : "Add Activity"}>
        {/* @ts-ignore */}
      <ActivitySection activity={activity} handleChange={handleChange} close={close}
        validate={validate} isValid={isValid} readOnly={readOnly} options={options} />
    </Modal>
  )
}
  
const ActivityPanel = ({activity, handleChange, close, validate, isValid, readOnly, options}: 
  {activity: ActivityRecord, forId: string, caseId: string, visible: boolean, handleChange: (o: object) => void, close: () => void,
  validate: (l: string, v: string) => boolean, isValid: boolean, readOnly: boolean, options: string[] | {label: string, value: string}[]
  }) => {

  function create() {        
    if (isValid) {
        close()
        // @ts-ignore
        activity.CaseName = null
        upsert('Activity', activity)
        handleChange({Type: "Note",  Description: ""} as ActivityRecord)
    }
  }

  return (
    <Container data-panel
      header={
        <Header variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs"> 
              <Button onClick={() => create()}>Add Activity</Button>
          </SpaceBetween>
          }
        > New Activity </Header>
      }>
        <ActivitySection activity={activity} handleChange={handleChange} 
          validate={validate} readOnly={readOnly} options={options} />
    </Container>
  )
}
  
export const ActivitySection = ({activity, handleChange, validate, readOnly, options}: 
  {activity: ActivityRecord, handleChange: (o: object) => void, 
  validate: (l: string, v: string) => boolean, readOnly: boolean, options: string[] | {label: string, value: string}[]
  }) => {

  return (
        <SpaceBetween direction="vertical" size="m">
          {!activity?.CaseId ? <Alert header="This event will not be assigned to a Case" type="error" /> : 
            <Alert header="This event will be assigned to a Case" type="success" />}
          {!activity?.ForId?.startsWith("Litigation") && <Alert header="This event will not be assigned to a Litigation" statusIconAriaLabel="Info" />}
          <Alert header={`This event will be assigned to ${userName}`} type="success" />
          <DateTimeInput label="Date & Time" value={activity?.On} validate={validate} readOnly={readOnly}
              onChange={ (value) => { handleChange({On: value}) } } />
          {/* <DateTimeInput label="Date & Time" value={activity?.On} readOnly={true} /> */}
          <ComboInput label="Type" value={activity.Type} validate={validate} readOnly={readOnly}
              options={options}
              onChange={ (v) => handleChange({Type: v}) } />
          <LabeledTextarea label="Description" rows={12} value={activity.Description} validate={validate} readOnly={readOnly}
              onChange={ value => handleChange({Description: value}) } />
        </SpaceBetween>
  )
}
